import React, { useState, useEffect } from 'react';
import { IPersonaProps } from '@fluentui/react';
import {
  TagPicker,
  TagPickerList,
  TagPickerControl,
  TagPickerProps,
  TagPickerOption,
  TagPickerGroup,
  TagPickerInput,
  Tag,
  Avatar,
  Field,
  FluentProvider,
  webLightTheme,
} from '@fluentui/react-components';
import { IServiceContact } from '../../companies/serviceContacts/interfaces';

export interface IPickerSuggestionsProps {
  noResultsFoundText?: string;
  suggestionsHeaderText?: string;
  loadingText?: string;
}

export interface IErrorHandlingPeoplePickerProps {
  controlName: string;
  labelText?: string;
  errorMessage?: string;
  required?: boolean;
  onChange?: (items: IPersonaProps[]) => void;
  onResolveSuggestions?: (filterText: string) => IPersonaProps[] | Promise<IPersonaProps[]>;
  onEmptyResolveSuggestions?: (
    currentPersonas?: IPersonaProps[],
  ) => IPersonaProps[] | Promise<IPersonaProps[]>;
  itemLimit?: number;
  selectedItems?: IPersonaProps[];
  defaultSelectedItems?: IPersonaProps[];
  disabled?: boolean;
  removeButtonAriaLabel?: string;
  resolveDelay?: number;
  onBlur?: () => void;
  className?: string;
  pickerSuggestionsProps?: IPickerSuggestionsProps;
}

export const addLabelToInvalidPeople = (people: IPersonaProps[]) => {
  return people?.map((person: IServiceContact) => {
    const persona: IServiceContact = {
      key: person.key,
      graphId: person.graphId,
      name: person.name,
      text: person.isInvalidUser ? `${person.text} (Invalid User)` : person.text,
      firstName: person.firstName,
      lastName: person.lastName,
      email: person.email,
      alias: person.alias,
      contactType: person.contactType,
    };
    return persona;
  });
};

const ErrorHandlingPeoplePicker = (props: IErrorHandlingPeoplePickerProps): JSX.Element => {
  const {
    controlName,
    labelText,
    errorMessage,
    required: isRequired,
    onChange,
    onResolveSuggestions,
    onEmptyResolveSuggestions,
    itemLimit,
    selectedItems,
    defaultSelectedItems,
    disabled,
    removeButtonAriaLabel,
    resolveDelay = 450,
    onBlur,
    className,
    pickerSuggestionsProps,
  } = props;

  const [selectedOptions, setSelectedOptions] = useState<string[]>([]);
  const [availableOptions, setAvailableOptions] = useState<IPersonaProps[]>([]);
  const [searchText, setSearchText] = useState('');
  const [selectedPeople, setSelectedPeople] = useState<Map<string, IPersonaProps>>(new Map());

  useEffect(() => {
    const items = selectedItems || defaultSelectedItems || [];
    const updatedItems = addLabelToInvalidPeople(items);

    const peopleMap = new Map<string, IPersonaProps>();
    const keys: string[] = [];

    updatedItems.forEach((item) => {
      const serviceContact = item as IServiceContact;
      const key = item.key?.toString() || serviceContact.graphId || `${item.text}-${item.email}`;
      peopleMap.set(key, item);
      keys.push(key);
    });

    setSelectedPeople(peopleMap);
    setSelectedOptions(keys);
  }, [selectedItems, defaultSelectedItems]);

  // Fetch user suggestions with debounce
  useEffect(() => {
    if (!onResolveSuggestions) return undefined;

    const timer = setTimeout(async () => {
      if (searchText) {
        try {
          const results = await onResolveSuggestions(searchText);
          setAvailableOptions(results || []);
        } catch {
          setAvailableOptions([]);
        }
      } else if (onEmptyResolveSuggestions) {
        try {
          const currentPeople = Array.from(selectedPeople.values());
          const results = await onEmptyResolveSuggestions(currentPeople);
          setAvailableOptions(results || []);
        } catch {
          setAvailableOptions([]);
        }
      } else {
        setAvailableOptions([]);
      }
    }, resolveDelay);

    return () => clearTimeout(timer);
  }, [searchText, onResolveSuggestions, onEmptyResolveSuggestions, resolveDelay, selectedPeople]);

  const onOptionSelect: TagPickerProps['onOptionSelect'] = (e, data) => {
    if (data.value === 'no-options') {
      return;
    }

    let newSelectedOptions = [...data.selectedOptions];

    // Apply item limit if specified
    if (itemLimit && newSelectedOptions.length > itemLimit) {
      newSelectedOptions = newSelectedOptions.slice(-itemLimit);
    }

    setSelectedOptions(newSelectedOptions);

    // Build selected people map
    const newPeopleMap = new Map(selectedPeople);
    const selectedPeopleArray: IPersonaProps[] = [];

    newSelectedOptions.forEach((optionKey) => {
      const person = newPeopleMap.get(optionKey);
      if (person) {
        selectedPeopleArray.push(person);
      } else {
        const option = availableOptions.find((opt) => {
          const serviceContact = opt as IServiceContact;
          const key = opt.key?.toString() || serviceContact.graphId || opt.text;
          return key === optionKey;
        });
        if (option) {
          newPeopleMap.set(optionKey, option);
          selectedPeopleArray.push(option);
        }
      }
    });

    setSelectedPeople(newPeopleMap);
    onChange?.(selectedPeopleArray);
  };

  const tagPickerOptions = availableOptions.filter((option) => {
    const serviceContact = option as IServiceContact;
    const key = option.key?.toString() || serviceContact.graphId || option.text;
    return !selectedOptions.includes(key);
  });

  const getDisplayName = (key: string): string => {
    return selectedPeople.get(key)?.text || key;
  };

  return (
    <FluentProvider theme={webLightTheme}>
      <Field
        label={labelText}
        required={isRequired}
        validationMessage={errorMessage}
        style={{ width: '100%' }}
        className={className}
      >
        <TagPicker
          onOptionSelect={onOptionSelect}
          selectedOptions={selectedOptions}
          disabled={disabled}
        >
          <TagPickerControl onBlur={onBlur} aria-label={labelText || controlName}>
            <TagPickerGroup aria-label={`Selected ${labelText || controlName}`}>
              {selectedOptions.map((option) => (
                <Tag
                  key={option}
                  media={<Avatar aria-hidden name={getDisplayName(option)} color="colorful" />}
                  value={option}
                  dismissible
                  aria-label={`${getDisplayName(option)} ${removeButtonAriaLabel || 'Remove'}`}
                >
                  {getDisplayName(option)}
                </Tag>
              ))}
            </TagPickerGroup>
            <TagPickerInput
              aria-label={`Search ${labelText || controlName}`}
              disabled={disabled}
              onChange={(e) => setSearchText((e.target as HTMLInputElement).value)}
            />
          </TagPickerControl>

          <TagPickerList>
            {tagPickerOptions.length > 0 ? (
              tagPickerOptions.map((option) => {
                const serviceContact = option as IServiceContact;
                const key = option.key?.toString() || serviceContact.graphId || option.text;
                return (
                  <TagPickerOption
                    key={key}
                    value={key}
                    secondaryContent={option.secondaryText || serviceContact.email}
                    media={<Avatar aria-hidden name={option.text} color="colorful" />}
                  >
                    {option.text}
                  </TagPickerOption>
                );
              })
            ) : (
              <TagPickerOption value="no-options">
                {pickerSuggestionsProps?.noResultsFoundText || 'No users found'}
              </TagPickerOption>
            )}
          </TagPickerList>
        </TagPicker>
      </Field>
    </FluentProvider>
  );
};

export default ErrorHandlingPeoplePicker;
